import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, MessageCircle, MapPin, Phone, UtensilsCrossed } from "lucide-react";
import { useProducts, useSite } from "@/lib/api";
import Seo from "@/components/common/Seo";
import SectionHeading from "@/components/common/SectionHeading";
import ProductCard from "@/components/products/ProductCard";
import ScenarioSection from "@/components/home/ScenarioSection";
import MasterStory from "@/components/home/MasterStory";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const { data: site } = useSite();
  const { data: products = [] } = useProducts();

  const featured = products.filter((p) => p.featured).slice(0, 6);
  const premium = products.filter((p) => p.is_premium).slice(0, 4);
  const rec = site?.weekly_recommendation;
  const recProducts = rec ? products.filter((p) => rec.products.includes(p.slug)) : [];

  const waUrl = site?.whatsapp
    ? `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(`Merhaba ${site.brand}, sipariş vermek istiyorum.`)}`
    : "#";

  const scrollToScenarios = () => {
    document.getElementById("senaryolar")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="home-page" className="bg-cream">
      <Seo
        description="1985'ten beri butik kasaplık. Günlük taze kesim et, premium steak'ler, mangal paketleri ve WhatsApp ile kolay sipariş. İyi Et Tesadüf Değildir."
        jsonLd={site ? {
          "@context": "https://schema.org",
          "@type": "Butcher",
          name: "Ertan Kasap",
          image: (process.env.REACT_APP_SITE_URL || window.location.origin) + "/products/scene-hero.webp",
          description: site.sub_slogan || site.slogan || "Butik kasaplık",
          telephone: site.phone_link || site.phone,
          address: { "@type": "PostalAddress", streetAddress: site.address, addressCountry: "TR" },
          url: (process.env.REACT_APP_SITE_URL || window.location.origin),
          priceRange: "₺₺",
          openingHours: (site.working_hours || []).map((h) => `${h.day} ${h.hours}`),
        } : undefined}
      />

      {/* HERO — tam ekran sinematik video, tek CTA */}
      <section data-testid="hero-section" className="relative flex min-h-[100svh] items-center justify-center overflow-hidden bg-charcoal">
        <video
          data-testid="hero-video"
          poster="/hero-butcher-poster.jpg"
          autoPlay loop muted playsInline preload="auto"
          aria-label="Ertan Kasap usta kasap et hazırlama"
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/hero-butcher.webm" type="video/webm" />
          <source src="/hero-butcher.mp4" type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-charcoal/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/25 to-charcoal/60" />

        <span className="absolute right-5 top-28 z-10 hidden h-20 w-20 flex-col items-center justify-center rounded-full border-2 border-cream/80 bg-bordo/90 font-mono text-[10px] uppercase leading-tight text-cream backdrop-blur-sm sm:flex lg:top-36">
          Günlük<br />Taze<br /><span className="text-[8px]">— 1985 —</span>
        </span>

        <motion.div initial="hidden" animate="show" className="relative z-10 mx-auto flex max-w-3xl flex-col items-center px-4 text-center sm:px-6">
          <motion.div variants={fadeUp} custom={0} className="mb-7 flex items-center gap-3">
            <span className="h-px w-8 bg-copper sm:w-12" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-copper sm:text-xs">{site?.hero?.eyebrow || "1985’ten beri butik kasaplık"}</span>
            <span className="h-px w-8 bg-copper sm:w-12" />
          </motion.div>
          <motion.h1 variants={fadeUp} custom={1} className="font-serif text-5xl leading-[0.95] tracking-tight text-cream sm:text-6xl lg:text-7xl">
            {site?.hero?.title_top || "İyi Et"} <span className="mt-1 block italic text-copper">{site?.hero?.title_bottom || "Tesadüf Değildir"}</span>
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} className="mt-8 max-w-xl text-base leading-relaxed text-cream/85 sm:text-lg">
            {site?.hero?.subtitle || "1985’ten beri aynı titizlik, aynı ustalık, aynı güven. Günlük taze ve özenle seçilmiş etler."}
          </motion.p>
          <motion.div variants={fadeUp} custom={3} className="mt-10 flex w-full justify-center sm:w-auto">
            <button
              onClick={scrollToScenarios}
              data-testid="hero-build-cta"
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-sm bg-copper px-8 py-4 text-base font-bold uppercase tracking-wide text-charcoal transition-transform hover:scale-[1.02] sm:w-auto"
            >
              <UtensilsCrossed className="h-5 w-5" /> Sofranı Kur
            </button>
          </motion.div>
          <motion.p variants={fadeUp} custom={4} className="mt-4 font-mono text-[11px] uppercase tracking-wider text-cream/45">
            Sofranı seç · listeni oluştur · WhatsApp’tan gönder
          </motion.p>
        </motion.div>

        <button
          onClick={scrollToScenarios}
          aria-label="Aşağı kaydır"
          className="absolute bottom-7 left-1/2 z-10 -translate-x-1/2 text-cream/70 transition-colors hover:text-copper"
        >
          <ChevronDown className="h-6 w-6 animate-bounce" />
        </button>
      </section>

      {/* 1 · SOFRA SENARYOLARI (Mangal Hesaplayıcı buraya gömülü) */}
      <ScenarioSection />

      {/* 2 · ÖNE ÇIKAN ÜRÜNLER */}
      <section className="border-y border-dashed border-bordo/30 bg-paper">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mb-12 flex items-end justify-between gap-4">
            <SectionHeading eyebrow="Vitrin · No.01" title="Öne Çıkan Ürünler" />
            <Link to="/urunler" className="hidden items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-bordo transition-all hover:gap-2.5 sm:inline-flex">
              Tümünü Gör <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </div>
      </section>

      {/* 3 · PREMIUM KOLEKSİYON — koyu bölüm */}
      <section className="bg-charcoal">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mb-12 flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-end">
            <SectionHeading tone="dark" eyebrow="Şefin Seçimi · No.02" title="Premium Koleksiyon" description="Tomahawk, Dry Aged, T-Bone, Short Rib ve Brisket. Özel günler için en seçkin etler." />
            <Link to="/urunler" className="inline-flex items-center gap-1.5 rounded-sm border border-copper/50 px-5 py-2.5 font-mono text-xs uppercase tracking-wider text-copper transition-colors hover:bg-copper hover:text-charcoal">
              Tüm Ürünleri Gör <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {premium.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </div>
      </section>

      {/* 4 · USTANIN ÖNERİSİ */}
      {rec && (
        <section data-testid="ustanin-onerisi" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid border border-dashed border-bordo/30 lg:grid-cols-5">
            <div className="flex flex-col justify-center bg-bordo p-8 text-cream lg:col-span-2 lg:p-12">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-cream/70">Ustanın Önerisi</span>
              <h3 className="mt-5 font-serif text-4xl leading-tight text-cream">{rec.title}</h3>
              <p className="mt-4 text-base leading-relaxed text-cream/80">{rec.text}</p>
            </div>
            <div className="grid grid-cols-2 gap-px bg-bordo/20 lg:col-span-3">
              {recProducts.map((p) => (
                <Link key={p.slug} to={`/urun/${p.slug}`} className="group relative aspect-square overflow-hidden bg-charcoal">
                  <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 to-transparent" />
                  <span className="absolute bottom-3 left-4 font-serif text-xl text-cream">{p.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 5 · HİKAYEMİZ */}
      <MasterStory />

      {/* 6 · İLETİŞİM */}
      <section className="border-t border-dashed border-bordo/30 bg-paper">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="grid gap-px border border-dashed border-bordo/20 bg-bordo/10 sm:grid-cols-3">
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 bg-paper p-6 transition-colors hover:bg-cream">
              <MessageCircle className="h-7 w-7 text-[#25D366]" />
              <div>
                <span className="block font-mono text-[10px] uppercase tracking-wider text-charcoal/50">WhatsApp</span>
                <span className="font-mono font-bold text-charcoal">{site?.whatsapp_display}</span>
              </div>
            </a>
            <a href={`tel:${site?.phone_link}`} className="flex items-center gap-4 bg-paper p-6 transition-colors hover:bg-cream">
              <Phone className="h-7 w-7 text-bordo" />
              <div>
                <span className="block font-mono text-[10px] uppercase tracking-wider text-charcoal/50">Telefon</span>
                <span className="font-mono font-bold text-charcoal">{site?.phone}</span>
              </div>
            </a>
            <Link to="/iletisim" className="flex items-center gap-4 bg-paper p-6 transition-colors hover:bg-cream">
              <MapPin className="h-7 w-7 text-bordo" />
              <div>
                <span className="block font-mono text-[10px] uppercase tracking-wider text-charcoal/50">Mağaza</span>
                <span className="font-mono font-bold text-charcoal">Beşiktaş / İstanbul</span>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
